import type { AdFreeReason } from '../../shared/types/adEligibility';
import { logger } from './logger';

const AD_SCRIPT_SELECTOR =
  'script[src*="googlesyndication"], script[src*="adsbygoogle"], script[src*="fundingchoicesmessages"]';
const AD_ELEMENT_SELECTOR =
  'ins.adsbygoogle, iframe[src*="googlesyndication"], iframe[src*="doubleclick"], iframe[name="googlefcPresent"]';

declare global {
  interface Window {
    __APT_ADS_BLOCKED__?: boolean;
    adsbygoogle?: unknown;
    googlefc?: {
      controlledMessagingFunction?: (m: unknown) => void;
      [key: string]: unknown;
    };
  }
}

export function cacheAdEligibilityLocal(showAds: boolean, hasPremium: boolean): void {
  try {
    localStorage.setItem('premiumStatus', hasPremium ? 'true' : 'false');
    localStorage.setItem('adEligibilityShowAds', showAds ? 'true' : 'false');
  } catch {
    // private browsing
  }
}

export function clearAdEligibilityLocalCache(): void {
  try {
    localStorage.removeItem('premiumStatus');
    localStorage.removeItem('adEligibilityShowAds');
  } catch {
    // ignore
  }
}

/** Last server-resolved showAds from /api/subscription/status (null if unset). Hint only until status returns. */
export function readCachedShowAds(): boolean | null {
  try {
    const v = localStorage.getItem('adEligibilityShowAds');
    if (v === 'true') {
      return true;
    }
    if (v === 'false') {
      return false;
    }
  } catch {
    // private browsing
  }
  return null;
}

/** Removable block — always configurable:true so free users can recover without cache clear. */
export function blockAdsbygoogleRemovable(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    Object.defineProperty(window, 'adsbygoogle', {
      value: undefined,
      writable: true,
      configurable: true,
      enumerable: false,
    });
  } catch {
    try {
      window.adsbygoogle = undefined;
    } catch {
      // ignore
    }
  }
  window.__APT_ADS_BLOCKED__ = true;
}

export function restoreFreeUserAdGlobals(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.__APT_ADS_BLOCKED__ = false;
    const adsbygoogleDescriptor = Object.getOwnPropertyDescriptor(window, 'adsbygoogle');
    if (adsbygoogleDescriptor?.configurable) {
      try {
        delete window.adsbygoogle;
      } catch (e) {
        logger.warn('[applyAdFreeDomBlocking] Could not unblock window.adsbygoogle:', e);
      }
    } else if (
      adsbygoogleDescriptor &&
      adsbygoogleDescriptor.configurable === false &&
      adsbygoogleDescriptor.value === undefined
    ) {
      // Legacy permanent lock from older builds — cannot recover in-place.
      logger.warn(
        '[applyAdFreeDomBlocking] window.adsbygoogle locked configurable:false (legacy) — hard refresh may be needed',
      );
    }

    const googlefcDescriptor = Object.getOwnPropertyDescriptor(window, 'googlefc');
    if (googlefcDescriptor?.configurable) {
      try {
        delete window.googlefc;
        window.googlefc = {};
      } catch (e) {
        logger.warn('[applyAdFreeDomBlocking] Could not restore window.googlefc:', e);
      }
    } else if (!googlefcDescriptor) {
      window.googlefc = {};
    } else if (googlefcDescriptor.configurable === false) {
      logger.warn(
        '[applyAdFreeDomBlocking] window.googlefc locked configurable:false (legacy)',
      );
    }
  } catch (e) {
    logger.warn('[applyAdFreeDomBlocking] Error restoring ad globals for free user:', e);
  }
}

function blockAdGlobalsAndDom(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    blockAdsbygoogleRemovable();
    const w = window;
    w.googlefc = w.googlefc || {};
    w.googlefc.controlledMessagingFunction = function (m: unknown) {
      const msg = m as { proceed?: (v: boolean) => void; dismiss?: () => void };
      if (msg && typeof msg.proceed === 'function') {
        msg.proceed(false);
      } else if (msg && typeof msg.dismiss === 'function') {
        msg.dismiss();
      }
    };
    // Keep googlefc configurable so free users can restore.
    try {
      Object.defineProperty(window, 'googlefc', {
        value: w.googlefc,
        writable: true,
        configurable: true,
        enumerable: false,
      });
    } catch {
      // already assigned
    }
    const scripts = document.querySelectorAll(AD_SCRIPT_SELECTOR);
    scripts.forEach((s) => s.remove());
    const adElements = document.querySelectorAll(AD_ELEMENT_SELECTOR);
    adElements.forEach((el) => el.remove());
  } catch (error) {
    logger.error('[applyAdFreeDomBlocking] Error executing immediate ad blocking:', error, {});
  }
}

/** Apply or clear DOM / window ad blocking based on server-resolved reasons. */
export function applyAdFreeDomBlocking(reasons: AdFreeReason[]): void {
  if (typeof document === 'undefined') {
    return;
  }

  const isAdFree = reasons.length > 0;

  if (isAdFree) {
    if (reasons.includes('premium')) {
      document.documentElement.setAttribute('data-premium-user', 'true');
    } else {
      document.documentElement.removeAttribute('data-premium-user');
    }
    if (reasons.includes('k9_role')) {
      document.documentElement.setAttribute('data-k9-user', 'true');
    } else {
      document.documentElement.removeAttribute('data-k9-user');
    }
    document.documentElement.setAttribute('data-adsense-blocked', 'true');
    document.documentElement.setAttribute('data-funding-choices-blocked', 'true');
    blockAdGlobalsAndDom();
    return;
  }

  document.documentElement.removeAttribute('data-premium-user');
  document.documentElement.removeAttribute('data-k9-user');
  document.documentElement.removeAttribute('data-adsense-blocked');
  document.documentElement.removeAttribute('data-funding-choices-blocked');
  restoreFreeUserAdGlobals();
}
